import React, { useState, useEffect, useMemo, useLayoutEffect, useRef } from 'react';
import moment from 'moment-timezone';
import { usePermissions } from '../../../hooks/usePermissions';
import { PermissionDenied } from "../../shared/PermissionDenied";
import styled from "@emotion/styled";
import { Box, Button, CircularProgress, Typography, IconButton } from "@mui/material";
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { ParentPageWrapper } from "../../shared/ParentPageWrapper";
import { StyledHeader, SplitContainer } from "../../../styles/commonStyles";
import { SelectTypePanel, SubTypePanel, DateSelectionPanel, SortByPanel, PaneContainer } from "./ReportPanels";

import { useQuery, useMutation } from '@tanstack/react-query';
import { getWithQueryApiServices, postWithQueryApiServices } from '../../../apis/api';
import { useOutletContext } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Dialog, DialogTitle, DialogContent, Grid, Chip } from '@mui/material';

const BoxContainer = styled(Box)(({ theme }) => ({
  padding: "10px",
  [theme.breakpoints.up("sm")]: {
    padding: "24px",
  },
  [theme.breakpoints.up("md")]: {
    padding: "1%",
  },
  boxSizing: "border-box",
}));

const ParentContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  alignItems: "start",
  justifyContent: "start",
  gap: "24px",
  padding: "20px 0",
  width: "100%",
  boxSizing: "border-box",
  overflow: "visible",
}));

const formatTimeAMPM = (dateStr) => {
  if (!dateStr) return '';
  try {
    const parts = dateStr.split(' ');
    if (parts.length < 2) return '';
    const timeParts = parts[1].split(':');
    let hours = parseInt(timeParts[0], 10);
    const minutes = timeParts[1];
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${hours}:${minutes} ${ampm}`;
  } catch (e) {
    return '';
  }
};

const getColumns = (type, subType, sortBy, onDownload, onActionClick) => {
  const getZoneAbbr = () => {
    try {
      const guess = moment.tz.guess();
      if (guess) {
        const zone = moment.tz.zone(guess);
        if (zone) {
          const abbr = zone.abbr(Date.now());
          if (abbr && !abbr.startsWith('+') && !abbr.startsWith('-')) {
            return abbr;
          }
        }
      }
      const parts = new Intl.DateTimeFormat('en', { timeZoneName: 'short' }).formatToParts(new Date());
      const tzPart = parts.find(p => p.type === 'timeZoneName');
      if (tzPart && tzPart.value) {
        return tzPart.value;
      }
      if (guess) {
        return guess.split('/').pop()?.replace(/_/g, ' ') || 'Local';
      }
      return 'Local';
    } catch (e) {
      return 'Local';
    }
  };
  const zoneAbbr = getZoneAbbr();
  const dateTimeHeader = `Date-Time (${zoneAbbr})`;
  const timeHeader = `Time (${zoneAbbr})`;

  const downloadCol = {
    field: 'actions',
    headerName: 'Download',
    flex: 0.6,
    sortable: false,
    renderCell: (params) => {
      if (!params.row.r2_key) {
        return <Typography variant="caption" color="text.secondary">No file attached</Typography>;
      }
      return (
        <IconButton 
          size="small" 
          color="primary" 
          onClick={(e) => {
            e.stopPropagation();
            onDownload(params.row.r2_key);
          }}
        >
          <CloudDownloadIcon fontSize="small" />
        </IconButton>
      );
    }
  };

  if (type === 'File Traffic') {
    const cols = [
      { field: 'dateTime', headerName: dateTimeHeader, flex: 1.2, valueFormatter: (params) => {
         if (!params.value) return '';
         return moment.utc(params.value).local().format('YYYY-MM-DD h:mm A');
      }},
      { field: 'fileType', headerName: 'File Type', flex: 0.8 },
    ];
    
    if (subType === 'Choose File Count' || subType === 'Choose Both') {
      cols.push({ field: 'fileCount', headerName: 'Count', flex: 0.6 });
    }
    
    if (subType === 'Choose File Size' || subType === 'Choose Both') {
      cols.push({ field: 'totalSize', headerName: 'Size (Bytes)', flex: 0.8 });
    }
    
    cols.push(downloadCol);
    return cols;
  }

  if (type === 'Exported Data') {
    return [
      { field: 'tenant', headerName: 'Tenant', flex: 1 },
      {
        field: 'dateTime',
        headerName: dateTimeHeader,
        flex: 1.2,
        valueFormatter: (params) => {
          if (!params.value) return '';
          return moment.utc(params.value).local().format('YYYY-MM-DD h:mm A');
        }
      },
      { field: 'user', headerName: 'User', flex: 1 },
      {
        field: 'exportType',
        headerName: 'Export Type',
        flex: 0.9,
        valueFormatter: (params) => {
          if (params.value === 'bulk_export') return 'Bulk Export';
          if (params.value === 'pdf_download') return 'PDF Download';
          return params.value || '';
        }
      },
      { field: 'fileCount', headerName: 'Files', flex: 0.5 },
      { field: 'ip', headerName: 'IP', flex: 0.9 },
    ];
  }

  const base = [
    { field: 'company', headerName: 'Company', flex: 1 },
    { 
      field: 'dateTime', 
      headerName: dateTimeHeader, 
      flex: 1.2,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return moment.utc(params.value).local().format('YYYY-MM-DD h:mm A');
      }
    },
  ];

  if (type === 'Auditing' || type === 'User') {
    const cols = [...base];
    
    if (sortBy === 'User then Date') {
      // Omit User column inside the table as it's the group header on top
    } else if (sortBy === 'Date then User') {
       const dtIndex = cols.findIndex(c => c.field === 'dateTime');
       if (dtIndex > -1) cols[dtIndex] = { field: 'dateTime', headerName: timeHeader, flex: 0.8, valueFormatter: (params) => {
         if (!params.value) return '';
         return moment.utc(params.value).local().format('h:mm A');
       }};
       cols.splice(dtIndex + 1, 0, { field: 'user', headerName: 'Username', flex: 1 });
    } else {
       cols.push({ field: 'user', headerName: 'User', flex: 1 });
    }

    return [
      ...cols,
      { field: 'eventType', headerName: 'Event', flex: 1 },
      { 
        field: 'detail', 
        headerName: 'Details', 
        flex: 2,
        renderCell: (params) => {
          const row = params.row;
          const value = params.value || '';
          const isQueryStringSearch = value.toLowerCase().includes('searchtype=');
          const isSearch = value.toLowerCase().includes('search performed') || isQueryStringSearch;
          const fileIdMatch = value.match(/(?:file\s+ID\s+|pictureID=|\bID:\s*)([a-zA-Z0-9-]+)/i);
          const pictureID = fileIdMatch ? fileIdMatch[1] : null;

          let displayElement;
          if (isSearch) {
            displayElement = (
              <Typography 
                variant="body2" 
                onClick={() => onActionClick(row)}
                sx={{ 
                  color: '#1976d2', 
                  cursor: 'pointer', 
                  textDecoration: 'underline',
                  '&:hover': { color: '#115293' },
                  fontWeight: 500,
                  fontSize: '0.875rem'
                }}
              >
                {value}
              </Typography>
            );
          } else if (pictureID) {
            displayElement = (
              <Typography 
                variant="body2" 
                onClick={() => onActionClick(row)}
                sx={{ 
                  color: '#2e7d32', 
                  cursor: 'pointer', 
                  textDecoration: 'underline',
                  '&:hover': { color: '#1b5e20' },
                  fontWeight: 500,
                  fontSize: '0.875rem'
                }}
              >
                {value}
              </Typography>
            );
          } else {
            displayElement = <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{value}</Typography>;
          }

          const hasCategory = row.categoryName;
          let criteriaContent = "";
          if (row.criteriaTXT) {
            try {
              const parsed = JSON.parse(row.criteriaTXT);
              criteriaContent = Object.entries(parsed)
                .map(([k, v]) => `${k}: ${v}`)
                .join(', ');
            } catch (e) {
              criteriaContent = row.criteriaTXT;
            }
          }

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5, whiteSpace: 'normal' }}>
              {displayElement}
              {hasCategory && (
                <Typography variant="caption" sx={{ color: '#555', mt: 0.5, fontStyle: 'italic', fontSize: '0.75rem' }}>
                  <strong>Category:</strong> {row.categoryName} {criteriaContent ? `| <strong>Criteria:</strong> ${criteriaContent}` : ''}
                </Typography>
              )}
            </Box>
          );
        }
      },
    ];
  }

  if (type === 'Category') {
    const cols = [...base];
    
    if (sortBy === 'User then Date') {
       // Omit Uploaded By column inside table as it's the group header on top
    } else if (sortBy === 'Date then User') {
       const dtIndex = cols.findIndex(c => c.field === 'dateTime');
       if (dtIndex > -1) cols[dtIndex] = { field: 'dateTime', headerName: timeHeader, flex: 0.8, valueFormatter: (params) => {
         if (!params.value) return '';
         return moment.utc(params.value).local().format('h:mm A');
       }};
       cols.splice(dtIndex + 1, 0, { field: 'uploadedBy', headerName: 'Username', flex: 1 });
    } else {
       cols.push({ field: 'uploadedBy', headerName: 'Uploaded By', flex: 1 });
    }

    return [
      ...cols,
      { field: 'category', headerName: 'Category', flex: 1 },
      { field: 'detail', headerName: 'Status', flex: 0.8 },
      downloadCol
    ];
  }

  return base;
};

const groupData = (data, sortBy) => {
  if (!sortBy) return { 'Results': data };
  
  const groups = {};
  data.forEach(row => {
    let key = "Other";
    if (sortBy === 'Date then User') {
      try {
        const d = new Date(row.dateTime.replace(' ', 'T'));
        if (isNaN(d.getTime())) throw new Error();
        key = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
      } catch {
        key = row.dateTime.split(' ')[0];
      }
    } else if (sortBy === 'User then Date') {
      key = row.user || row.uploadedBy || "System";
    }
    
    if (!groups[key]) groups[key] = [];
    groups[key].push(row);
  });
  return groups;
};

export const Reporting = () => {
  const { currentCompany, tenantString, registerCloseHandler } = useOutletContext();
  const { hasPermission, isGlobalSuperAdmin, isTenantAdmin, isSystemAdmin } = usePermissions();
  
  const canReadReport = isGlobalSuperAdmin || isTenantAdmin || hasPermission("Reporting: Read");
  const canSeeExportedData = isGlobalSuperAdmin || isTenantAdmin || isSystemAdmin;
  const reportTypeOptions = canSeeExportedData
    ? ["Auditing", "Category", "File Traffic", "User", "Exported Data"]
    : ["Auditing", "Category", "File Traffic", "User"];

  const tourRun = useSelector((state) => state.mainReducer?.tourRun);
  const activeTour = useSelector((state) => state.mainReducer?.activeTour);
  const tourStepIndex = useSelector((state) => state.mainReducer?.tourStepIndex);
  const isReportingTourActive = tourRun && activeTour === 'reporting';

  const [reportType, setReportType] = useState(null);
  const [subType, setSubType] = useState(null);
  const [dateMode, setDateMode] = useState(null);
  const [dateDone, setDateDone] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState(null);
  const [generated, setGenerated] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageSize = 100;
  const [columnWidth, setColumnWidth] = useState(250);
  const col2Ref = useRef(null);

  // PDF Preview and Action States
  const [openPDF, setOpenPDF] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const closePDFModal = () => {
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setOpenPDF(false);
  };

  const handleActionClick = async (row) => {
    const value = row.detail || '';
    const isQueryStringSearch = value.toLowerCase().includes('searchtype=');
    const isSearch = value.toLowerCase().includes('search performed') || isQueryStringSearch;
    const fileIdMatch = value.match(/(?:file\s+ID\s+|pictureID=|\bID:\s*)([a-zA-Z0-9-]+)/i);
    const pictureID = fileIdMatch ? fileIdMatch[1] : null;

    if (isSearch) {
      if (isQueryStringSearch) {
        const urlParams = new URLSearchParams(value);
        const searchTypeParam = urlParams.get('searchtype') || 'qsearch';
        const searchQueryParam = urlParams.get('txtSearch') || '';

        if (searchTypeParam === 'psearch') {
          try {
            setPdfLoading(true);
            const selectedCategories = [];
            const criteriaValues = {};

            for (const [key, val] of urlParams.entries()) {
              if (key.startsWith('k_') && val === 'on') {
                const catId = parseInt(key.substring(2), 10);
                if (!isNaN(catId)) selectedCategories.push(catId);
              }
              if (key.includes('|')) {
                criteriaValues[key] = val;
              }
            }

            if (selectedCategories.length === 0) {
              for (const key of Object.keys(criteriaValues)) {
                const parts = key.split('|');
                if (parts.length >= 2) {
                  const catId = parseInt(parts[1], 10);
                  if (!isNaN(catId) && !selectedCategories.includes(catId)) {
                    selectedCategories.push(catId);
                  }
                }
              }
            }

            const payload = {
              searchQuery: searchQueryParam,
              selectedCompanies: [currentCompany],
              selectedCategories,
              pageNumber: 1,
              pageSize: 20
            };

            if (Object.keys(criteriaValues).length > 0) {
              payload.criteriaValues = criteriaValues;
            }

            const res = await postWithQueryApiServices('search/power/submit', `selectedCompany=${currentCompany}${tenantString || ""}`, payload);
            const searchId = Date.now().toString();
            const searchState = {
              data: res.data.results || [],
              totalCount: res.data.totalCount || 0,
              searchValue: searchQueryParam,
              workData: payload,
              searchType: "power",
              selectedCompanyId: currentCompany,
              tenantString
            };
            localStorage.setItem(`search_${searchId}`, JSON.stringify(searchState));
            window.open(`/searchResult?searchId=${searchId}&hideHeader=true`, "_blank");
          } catch (err) {
            console.error("Failed to run query-string power search from audit:", err);
          } finally {
            setPdfLoading(false);
          }
        } else {
          try {
            setPdfLoading(true);
            const fcat = urlParams.get('fcat') || '';
            const queryParams = `searchQuery=${encodeURIComponent(searchQueryParam)}&categoryID=${fcat}&selectedCompany=${currentCompany}${tenantString || ""}&pageNumber=1&pageSize=20`;
            const res = await getWithQueryApiServices('search/quick/submit', queryParams);
            const searchId = Date.now().toString();
            const searchState = {
              data: res.data.results || [],
              totalCount: res.data.totalCount || 0,
              searchValue: searchQueryParam,
              queryParams,
              searchType: "quick",
              selectedCompanyId: currentCompany,
              tenantString
            };
            localStorage.setItem(`search_${searchId}`, JSON.stringify(searchState));
            window.open(`/searchResult?searchId=${searchId}&hideHeader=true`, "_blank");
          } catch (err) {
            console.error("Failed to run query-string quick search from audit:", err);
          } finally {
            setPdfLoading(false);
          }
        }
      } else {
        const isPower = value.toLowerCase().includes('power search performed');
        const queryMatch = value.match(/(?:Search performed:|performed:)\s*([^,]+)/i);
        const searchQuery = queryMatch ? queryMatch[1].trim() : '';

        if (isPower) {
          try {
            setPdfLoading(true);
            const payload = {
              searchQuery: searchQuery === 'No query' ? '' : searchQuery,
              selectedCompanies: [currentCompany],
              selectedCategories: [],
              pageNumber: 1,
              pageSize: 20
            };
            const res = await postWithQueryApiServices('search/power/submit', `selectedCompany=${currentCompany}${tenantString || ""}`, payload);
            const searchId = Date.now().toString();
            const searchState = {
              data: res.data.results || [],
              totalCount: res.data.totalCount || 0,
              searchValue: searchQuery,
              workData: payload,
              searchType: "power",
              selectedCompanyId: currentCompany,
              tenantString
            };
            localStorage.setItem(`search_${searchId}`, JSON.stringify(searchState));
            window.open(`/searchResult?searchId=${searchId}&hideHeader=true`, "_blank");
          } catch (err) {
            console.error("Failed to run power search from audit:", err);
          } finally {
            setPdfLoading(false);
          }
        } else {
          try {
            setPdfLoading(true);
            const catMatch = value.match(/Category:\s*([^,]+)/i);
            const categoryName = catMatch ? catMatch[1].trim() : '';
            let categoryID = '';
            if (categoryName && categoryName !== 'All') {
              const catsRes = await getWithQueryApiServices('category/summary', `selectedCompany=${currentCompany}${tenantString || ''}`);
              const matchedCat = (catsRes.data || []).find(c => c.name.toLowerCase() === categoryName.toLowerCase());
              if (matchedCat) categoryID = matchedCat.categoryID;
            }
            
            const queryParams = `searchQuery=${encodeURIComponent(searchQuery === 'No query' ? '' : searchQuery)}&categoryID=${categoryID}&selectedCompany=${currentCompany}${tenantString || ""}&pageNumber=1&pageSize=20`;
            const res = await getWithQueryApiServices('search/quick/submit', queryParams);
            const searchId = Date.now().toString();
            const searchState = {
              data: res.data.results || [],
              totalCount: res.data.totalCount || 0,
              searchValue: searchQuery,
              queryParams,
              searchType: "quick",
              selectedCompanyId: currentCompany,
              tenantString
            };
            localStorage.setItem(`search_${searchId}`, JSON.stringify(searchState));
            window.open(`/searchResult?searchId=${searchId}&hideHeader=true`, "_blank");
          } catch (err) {
            console.error("Failed to run quick search from audit:", err);
          } finally {
            setPdfLoading(false);
          }
        }
      }
    } else if (pictureID) {
      try {
        setPdfLoading(true);
        const getCookie = (name) => {
          const value = `; ${document.cookie}`;
          const parts = value.split(`; ${name}=`);
          if (parts.length === 2) return parts.pop().split(';').shift();
        };
        const token = getCookie('token') || localStorage.getItem('token');
        const baseUrl = process.env.REACT_APP_API_URL || '';
        const url = `${baseUrl}/api/search/quick/download?pictureID=${pictureID}&selectedCompany=${currentCompany}${tenantString || ""}`;
        
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!response.ok) throw new Error("Failed to download PDF");
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        setPdfBlobUrl(blobUrl);
        setOpenPDF(true);
      } catch (err) {
        console.error("Failed to fetch PDF from audit:", err);
      } finally {
        setPdfLoading(false);
      }
    }
  };

  // Pre-populate data during guided tour to show all panels
  useEffect(() => {
    if (isReportingTourActive) {
      setReportType("Auditing");
      setSubType("Search");
      setDateMode("Single Day");
      
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const todayStr = `${yyyy}-${mm}-${dd}`;
      setStartDate(todayStr);
      setEndDate(todayStr);
      
      setDateDone(true);
      setSortBy(null);
    }
  }, [isReportingTourActive]);

  useEffect(() => {
    if (registerCloseHandler) {
      registerCloseHandler(() => {
        if (generated) {
          setGenerated(false);
          return true; // Handled
        }
        return false; // Not handled, default to exit navigation
      });
      return () => {
        registerCloseHandler(null);
      };
    }
  }, [registerCloseHandler, generated]);

  const reset = () => {
    setReportType(null);
    setSubType(null);
    setDateMode(null);
    setDateDone(false);
    setStartDate('');
    setEndDate('');
    setSortBy(null);
    setGenerated(false);
    setReportData([]);
    setPageNumber(1);
    setHasMore(false);
  };

  const previousCompanyRef = useRef(currentCompany);
  useEffect(() => {
    if (String(previousCompanyRef.current ?? "") === String(currentCompany ?? "")) return;
    previousCompanyRef.current = currentCompany;
    reset();
  }, [currentCompany]);

  const needsSort = reportType === 'Auditing' || reportType === 'Category';

  const showSubType = isReportingTourActive || !!reportType;
  const showDate = isReportingTourActive || (!!reportType && !!subType);
  const showSort = isReportingTourActive || (needsSort && dateDone);

  const effectiveReportTypeForOptions = reportType || (isReportingTourActive ? "Auditing" : null);

  // Fetch SubType Options
  const { data: subOptions = [], isLoading: isLoadingOptions } = useQuery({
    queryKey: ['reportOptions', effectiveReportTypeForOptions, currentCompany],
    queryFn: () => getWithQueryApiServices('reporting/options', `type=${effectiveReportTypeForOptions}&selectedCompany=${currentCompany}${tenantString || ''}`).then(res => res.data),
    enabled: !!effectiveReportTypeForOptions,
  });

  useLayoutEffect(() => {
    const measureText = (text) => {
      const span = document.createElement('span');
      span.style.visibility = 'hidden';
      span.style.position = 'absolute';
      span.style.whiteSpace = 'nowrap';
      span.style.fontSize = '0.85rem';
      span.style.fontWeight = '400';
      span.style.fontFamily = 'Roboto, sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI"';
      span.innerText = text;
      document.body.appendChild(span);
      const width = span.getBoundingClientRect().width;
      document.body.removeChild(span);
      return width;
    };

    const titleWidth = measureText(`${reportType || ''} SELECTION`);
    let maxContentWidth = titleWidth;
    
    subOptions.forEach(opt => {
      const rawText = typeof opt === 'object' ? opt?.name : opt;
      const cleanedText = typeof rawText === 'string' ? rawText.split("(")[0].trim() : (rawText || "");
      const w = measureText(cleanedText);
      if (w > maxContentWidth) maxContentWidth = w;
    });

    if (maxContentWidth > 0) {
      setColumnWidth(Math.max(250, maxContentWidth + 100));
    }
  }, [subOptions, reportType]);

  // Generate Report Mutation
  const generateMutation = useMutation({
    mutationFn: (payload) => postWithQueryApiServices('reporting/generate', `selectedCompany=${currentCompany}${tenantString || ''}`, payload).then(res => res.data),
    onSuccess: (response, variables) => {
      const data = Array.isArray(response) ? response : (response.results || []);
      const isAppend = variables.pageNumber > 1;
      console.log(`[Reporting] Received ${data.length} rows for page ${variables.pageNumber} (Append: ${isAppend})`);
      
      if (data.length > 0) {
        console.log(`[Reporting] Received IDs - First: ${data[0].id || data[0].pictureID}, Last: ${data[data.length-1].id || data[data.length-1].pictureID}`);
      }

      // Force globally unique IDs to prevent DataGrid from dropping rows 
      // if combined tables return identical primary keys.
      const uniqueData = data.map((item, index) => ({
        ...item,
        id: `${item.id || item.pictureID || 'row'}_p${variables.pageNumber}_i${index}`
      }));
      
      if (isAppend) {
        setReportData(prev => {
          const newData = [...prev, ...uniqueData];
          console.log(`[Reporting] Total rows in state: ${newData.length}`);
          return newData;
        });
      } else {
        setReportData(uniqueData);
      }
      
      setHasMore(data.length === pageSize);
      setGenerated(true);
      setLoadingMore(false);
    },
    onError: () => {
      setLoadingMore(false);
    }
  });

  const handleDownload = (key) => {
    // Construct the full URL for the download
    const baseUrl = process.env.REACT_APP_API_URL || 'https://ben.bishnoi.live';
    
    // Get token from cookies or local storage
    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
    };
    const token = getCookie('token') || localStorage.getItem('token');

    const url = `${baseUrl}/api/upload/imaging/bulkscan/getFile?filePath=${encodeURIComponent(key)}${token ? `&token=${token}` : ''}`;
    window.open(url, '_blank');
  };

  const handleGenerate = (finalSortBy = sortBy, page = 1, customParams = null) => {
    const isLoadMore = page > 1;
    if (isLoadMore) setLoadingMore(true);
    else setPageNumber(1);

    generateMutation.mutate({
      reportType: customParams && customParams.reportType !== undefined ? customParams.reportType : reportType,
      subType: customParams && customParams.subType !== undefined ? customParams.subType : subType,
      dateMode: customParams && customParams.dateMode !== undefined ? customParams.dateMode : dateMode,
      startDate: customParams && customParams.startDate !== undefined ? customParams.startDate : startDate,
      endDate: customParams && customParams.endDate !== undefined ? customParams.endDate : endDate,
      sortBy: finalSortBy,
      selectedCompany: customParams && customParams.selectedCompany !== undefined ? customParams.selectedCompany : currentCompany,
      pageNumber: page,
      pageSize: pageSize
    });
  };



  const handleLoadMore = () => {
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    handleGenerate(sortBy, nextPage);
  };

  if (isLoadingOptions && !!effectiveReportTypeForOptions) {
    return (
      <ParentPageWrapper>
        <BoxContainer sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 15, gap: 2 }}>
          <CircularProgress size={60} thickness={4} sx={{ color: '#1a237e' }} />
          <Typography variant="body1" sx={{ color: '#666', fontWeight: 500, fontFamily: 'Poppins' }}>
            Loading reporting...
          </Typography>
        </BoxContainer>
      </ParentPageWrapper>
    );
  }

  if (!canReadReport) {
    return (
      <ParentPageWrapper>
        <PermissionDenied message="You do not have permission to view or generate reports. Please contact your administrator." />
      </ParentPageWrapper>
    );
  }

  const renderExportedDataSummary = () => {
    if (reportType !== 'Exported Data' || !reportData || reportData.length === 0) return null;
    const totalFiles = reportData.reduce((sum, row) => sum + parseInt(row.fileCount || 0, 10), 0);
    return (
      <Box sx={{
        p: 2.5,
        mb: 3,
        backgroundColor: '#f5f7fa',
        borderRadius: '8px',
        borderLeft: '4px solid #ed6c02',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a365d', mb: 1 }}>
          Exported Data Summary
        </Typography>
        <Typography variant="body2" color="textSecondary">Files in loaded rows</Typography>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#c05621', mt: 0.5 }}>{totalFiles}</Typography>
      </Box>
    );
  };

  const renderFileTrafficSummary = () => {
    if (reportType !== 'File Traffic' || !reportData || reportData.length === 0) return null;
    
    let totalCount = 0;
    let totalSizeBytes = 0;
    const typeCounts = {};
    
    reportData.forEach(row => {
      totalCount += parseInt(row.fileCount || 1, 10);
      totalSizeBytes += parseInt(row.totalSize || 0, 10);
      const ft = row.fileType || 'Unknown';
      typeCounts[ft] = (typeCounts[ft] || 0) + 1;
    });

    const totalSizeFormatted = totalSizeBytes > 1024 * 1024 
      ? `${(totalSizeBytes / (1024 * 1024)).toFixed(2)} MB`
      : `${(totalSizeBytes / 1024).toFixed(2)} KB`;

    let titleStr = "";
    if (dateMode === "Whole Year" && startDate) {
      titleStr = `File traffic for ${startDate.substring(0, 4)}`;
    } else if (dateMode === "Month / Year" && startDate) {
      const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
      const mIdx = parseInt(startDate.substring(5, 7), 10) - 1;
      titleStr = `File traffic for ${monthNames[mIdx]} ${startDate.substring(0, 4)}`;
    } else {
      titleStr = `File traffic from ${startDate} to ${endDate}`;
    }

    return (
      <Box sx={{ 
        p: 2.5, 
        mb: 3, 
        backgroundColor: '#f5f7fa', 
        borderRadius: '8px', 
        borderLeft: '4px solid #1976d2',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a365d', mb: 1 }}>
          {titleStr} Summary
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="textSecondary">Total Files Uploaded</Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2b6cb0', mt: 0.5 }}>{totalCount}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="textSecondary">Total Storage Size</Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2b6cb0', mt: 0.5 }}>{totalSizeFormatted}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="textSecondary">Format Distribution</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {Object.entries(typeCounts).map(([type, count]) => {
                const shortType = type.split('/')[1] || type;
                return (
                  <Chip 
                    key={type} 
                    label={`${shortType.toUpperCase()}: ${count}`} 
                    size="small" 
                    sx={{ backgroundColor: '#e2e8f0', color: '#4a5568', fontWeight: 500 }} 
                  />
                );
              })}
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <ParentPageWrapper>
      <BoxContainer>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 1
        }}>
          <StyledHeader style={{ margin: 0 }}>Reporting</StyledHeader>
          <Button id="tour-reporting-startover-btn" variant="contained" color="primary" onClick={reset} sx={{ marginRight: { xs: "36px", sm: "28px", md: "70px" } }}>START OVER</Button>
        </Box>

        <Box id="tour-reporting-results-container" sx={{ 
          mt: 3, 
          backgroundColor: 'white', 
          p: 2, 
          borderRadius: 2, 
          height: { xs: 'auto', md: 'calc(100vh - 250px)' }, 
          display: generated ? 'flex' : 'none', 
          flexDirection: 'column',
          overflow: 'hidden' 
        }}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
              Results: {reportType} - {subType}
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: '#666' }}>
              {reportData.length} record{reportData.length !== 1 ? 's' : ''} loaded
            </Typography>
          </Box>
          
          {/* File Traffic Summary View */}
          {renderFileTrafficSummary()}
          {renderExportedDataSummary()}

          <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
            {reportData.length === 0 ? (
              <Typography sx={{ color: '#555', fontSize: '1rem', py: 4, textAlign: 'center' }}>
                No records met your criteria.
              </Typography>
            ) : (() => {
              const groups = groupData(reportData, sortBy);
              const columns = getColumns(reportType, subType, sortBy, handleDownload, handleActionClick);
              return (
                <>
                  {Object.keys(groups).map((groupKey) => (
                    <Box key={groupKey} sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ 
                        color: sortBy === 'Date then User' ? '#d32f2f' : '#1976d2',
                        fontWeight: 'bold',
                        mb: 1,
                        fontSize: '1rem',
                        borderBottom: '1px solid #eee',
                        pb: 0.5
                      }}>
                        {groupKey}
                      </Typography>
                      <Box sx={{ width: '100%', overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f5f5f5' }}>
                              {columns.map((col) => (
                                <th key={col.field} style={{
                                  padding: '10px 16px',
                                  textAlign: 'left',
                                  fontWeight: 600,
                                  color: '#333',
                                  borderBottom: '2px solid #ddd',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {col.headerName}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {groups[groupKey].map((row, rowIndex) => (
                              <tr key={row.id || rowIndex} style={{
                                backgroundColor: rowIndex % 2 === 0 ? '#fff' : '#f9f9f9',
                                borderBottom: '1px solid #eee'
                              }}>
                                {columns.map((col) => (
                                  <td key={col.field} style={{
                                    padding: '8px 16px',
                                    color: '#555',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {col.renderCell
                                      ? col.renderCell({ row, value: row[col.field] })
                                      : col.valueFormatter
                                        ? col.valueFormatter({ value: row[col.field] })
                                        : row[col.field] ?? ''}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Box>
                    </Box>
                  ))}
                </>
              );
            })()}
          </Box>
          {hasMore && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              p: 2, 
              borderTop: '1px solid #eee',
              backgroundColor: '#fff'
            }}>
              <Button 
                variant="contained" 
                onClick={handleLoadMore} 
                disabled={loadingMore}
                startIcon={loadingMore ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{ minWidth: 200 }}
              >
                {loadingMore ? 'LOADING MORE...' : 'LOAD MORE RECORDS'}
              </Button>
            </Box>
          )}
        </Box>

        {!generated && (
          <ParentContainer sx={{ 
            gridTemplateColumns: {
              xs: "1fr",
              sm: `repeat(auto-fit, minmax(min(100%, ${columnWidth}px), ${columnWidth}px))`
            }
          }}>
            <SplitContainer id="tour-reporting-type-panel" style={{ backgroundColor: "transparent", width: '100%' }}>
              <PaneContainer 
                active={isReportingTourActive ? (tourStepIndex === 1) : !reportType} 
                disabled={isReportingTourActive ? false : !!reportType} 
                showRedo={!isReportingTourActive && !!reportType}
                onRedo={() => { setReportType(null); setSubType(null); setDateMode(null); setDateDone(false); setSortBy(null); }}
              >
                <SelectTypePanel 
                  types={reportTypeOptions}
                  onSelect={(v) => { setReportType(v); setSubType(null); setDateMode(null); setDateDone(false); setSortBy(null); }} 
                  selected={reportType} 
                  active={isReportingTourActive ? (tourStepIndex === 1) : !reportType}
                />
              </PaneContainer>
            </SplitContainer>

            {showSubType && (
              <SplitContainer id="tour-reporting-subtype-panel" style={{ backgroundColor: "transparent", width: '100%', minWidth: 'auto' }}>
                <PaneContainer 
                  active={isReportingTourActive ? (tourStepIndex === 2) : (!!reportType && !subType)} 
                  disabled={isReportingTourActive ? false : !!subType} 
                  showRedo={!isReportingTourActive && !!subType}
                  onRedo={() => { setSubType(null); setDateMode(null); setDateDone(false); setSortBy(null); }}
                >
                  {isLoadingOptions ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
                  ) : (
                  <div ref={col2Ref}>
                    <SubTypePanel 
                      reportType={reportType || (isReportingTourActive ? "Auditing" : "")} 
                      options={subOptions}
                      selected={subType} 
                      onSelect={(v) => { setSubType(v); setDateMode(null); setDateDone(false); setSortBy(null); }} 
                      active={isReportingTourActive ? (tourStepIndex === 2) : (!!reportType && !subType)}
                    />
                  </div>
                  )}
                </PaneContainer>
              </SplitContainer>
            )}

            {showDate && (
              <SplitContainer id="tour-reporting-date-panel" style={{ backgroundColor: "transparent", width: '100%', minWidth: 'auto' }}>
                <PaneContainer 
                  active={isReportingTourActive ? (tourStepIndex === 3) : (!!subType && !dateDone)} 
                  disabled={isReportingTourActive ? false : dateDone} 
                  showRedo={!isReportingTourActive && dateDone}
                  onRedo={() => { setDateMode(null); setDateDone(false); setSortBy(null); }}
                >
                  <DateSelectionPanel 
                    dateMode={dateMode} 
                    setDateMode={setDateMode} 
                    dateDone={dateDone} 
                    setDateDone={setDateDone}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    onSelect={(start, end) => { 
                      if(!needsSort) handleGenerate(); 
                    }} 
                    active={isReportingTourActive ? (tourStepIndex === 3) : (!!subType && !dateDone)}
                  />
                </PaneContainer>
              </SplitContainer>
            )}

            {showSort && (
              <SplitContainer id="tour-reporting-sort-panel" style={{ backgroundColor: "transparent", width: '100%', minWidth: 'auto' }}>
                <PaneContainer 
                  active={isReportingTourActive ? (tourStepIndex === 4) : true} 
                  disabled={isReportingTourActive ? false : false} 
                  showRedo={false}
                >
                  <SortByPanel 
                    onSelect={(v) => { setSortBy(v); handleGenerate(v); }} 
                    selected={sortBy}
                    active={isReportingTourActive ? (tourStepIndex === 4) : (dateDone && needsSort && !sortBy)}
                  />
                </PaneContainer>
              </SplitContainer>
            )}

            {generateMutation.isPending && (
              <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(255,255,255,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            )}
          </ParentContainer>
        )}
      </BoxContainer>

      {/* Action Backdrop Spinner */}
      {pdfLoading && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(255,255,255,0.7)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Executing action...</Typography>
        </Box>
      )}

      {/* PDF Preview Modal */}
      <Dialog 
        open={openPDF} 
        onClose={closePDFModal}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#eeeeee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          PDF Preview
          <Button onClick={closePDFModal} color="error">Close</Button>
        </DialogTitle>
        <DialogContent sx={{ minHeight: '80vh', p: 0 }}>
          {pdfBlobUrl ? (
            <iframe
              src={pdfBlobUrl}
              title="PDF Preview"
              width="100%"
              height="800px"
              style={{ border: 'none' }}
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </ParentPageWrapper>
  );
};